import React, { useState, useEffect} from 'react';
import { useCourt } from "../context/contextHooks";
import { getDecimal } from '../global/utils';
import { getServiceFee, getCaseLabel } from "./lookupFunctions";
import { useStyles } from "../styles/styles";
import { focusWhenExposed } from "../global/focus";
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import PaymentsTwoToneIcon from '@mui/icons-material/PaymentsTwoTone';
import ReplayIcon from "@mui/icons-material/Replay";
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import DeleteTwoTone from '@mui/icons-material/DeleteTwoTone';

const SelectedBalances = (props) => {
    const balances = props.balances;
    const plans = props.plans;
    const nonCourt = props.nonCourt;
    const splitFees = props.splitFees;
    const removeBalance = props.removeBalance;
    const removePlan = props.removePlan;
    const feeResponse = props.feeResponse;
    const clearSelected = props.clearSelected;
    const openPayment = props.openPayment;
    const setShowSelected = props.setShowSelected;
    const focusRef = props.focusRef;
    const [court,] = useCourt();
    const listTitle = props.listTitle;
    const classes = useStyles;
    const [serviceFee, setServiceFee] = useState(null);
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if ((balances && balances.length > 0) || (plans && plans.length > 0)) {
            setServiceFee(getServiceFee(balances, plans));
        }
   }, [balances, plans])

   // The drawer is modal, so focus has to wait until MUI stops hiding the page
   // behind it - see global/focus.js.
   const handleBack = () => {
       setShowSelected(false);
       focusWhenExposed(() => focusRef?.current);
   };

    return (
        <>
            <Typography variant='subtitle1' component='h2' color='primary' sx={{...classes.title, fontSize: '1.2rem', fontWeight: '500', marginTop: '1rem', marginBottom: '1rem'}}>
                {listTitle ? listTitle : 'Selected Items'}
            </Typography>
            {!smallScreen && (
                <Paper elevation={3} sx={{...classes.listContainer, maxHeight: '85vh', overflow: 'auto'}}>
                    <Box component='div' sx={{display: 'flex', flexDirection: 'column', margin: '1rem'}}>
                        <List sx={{...classes.list, maxHeight: '100%'}}>
                            {plans && plans.length > 0 && plans.map((plan) => (
                                <ListItem alignItems='flex-start' sx={{...classes.listPadding, borderBottom: '1px solid #e0e0e0'}} key={plan.planDescription}>
                                    <ListItemText
                                        sx={classes.listText}
                                        primary={
                                            <Grid container spacing={2} style={{width: '100%'}} sx={{
                                                alignItems: "top"
                                            }}>
                                                <Grid
                                                    size={{
                                                        xs: 5,
                                                        lg: 3
                                                    }}>
                                                    <Tooltip title='Remove payment plan from selected list' slots={{transition: Zoom}} arrow>
                                                        <Button variant='contained' color='error' onClick={() => removePlan(plan)} sx={classes.tinyButton} aria-label={`Remove ${plan.planDescription}`}>
                                                            Remove
                                                        </Button>
                                                    </Tooltip>
                                                </Grid>
                                                <Grid
                                                    size={{
                                                        xs: 7,
                                                        lg: 9
                                                    }}>
                                                    {plan.planDescription}
                                                </Grid>
                                            </Grid>
                                        }
                                        slotProps={{
                                            // div, not span/p: both slots hold Grid containers.
                                            primary: {component: 'div', variant: 'body2', color: 'textPrimary'},
                                            secondary: {component: 'div', variant: 'body2', color: 'textPrimary'}
                                        }}
                                        secondary={
                                            <Grid container spacing={2} style={{width: '100%'}} sx={{
                                                alignItems: "top"
                                            }}>
                                                <Grid size={12}>
                                                    {`Payment Amt.: ${getDecimal(plan.planPaymentAmount)}`}
                                                </Grid>
                                            </Grid>
                                        }
                                    />
                                </ListItem>
                            ))}
                            {balances && balances.length > 0 && balances.map((balance) => (
                                <ListItem alignItems='flex-start' sx={{...classes.listPadding, borderBottom: '1px solid slategrey'}} key={balance.caseNumber}>
                                    <ListItemText
                                        sx={classes.listText}
                                        primary={
                                            <Grid container spacing={2} style={{width: '100%'}} sx={{
                                                alignItems: "top"
                                            }}>
                                                <Grid
                                                    size={{
                                                        xs: 12,
                                                        sm: 5,
                                                        lg: 3
                                                    }}>
                                                    <Tooltip title='Remove balance from selected list' slots={{transition: Zoom}} arrow>
                                                        <Button variant='contained' color='error' onClick={() => removeBalance(balance)} sx={classes.tinyButton} aria-label={`Remove ${getCaseLabel(court)} ${balance.caseNumber}`}>
                                                            Remove
                                                        </Button>
                                                    </Tooltip>
                                                </Grid>
                                                <Grid
                                                    size={{
                                                        xs: 12,
                                                        sm: 5,
                                                        lg: 3
                                                    }}>
                                                    {`${getCaseLabel(court)} ${balance.caseNumber}`}
                                                </Grid>
                                                <Grid
                                                    size={{
                                                        xs: 12,
                                                        lg: 5
                                                    }}>
                                                    {balance.description}
                                                </Grid>
                                            </Grid>
                                        }
                                        slotProps={{
                                            // div, not span/p: both slots hold Grid containers.
                                            primary: {component: 'div', variant: 'body2', color: 'textPrimary'},
                                            secondary: {component: 'div', variant: 'body2', color: 'textPrimary'}
                                        }}
                                        secondary={
                                            <Grid container spacing={2} style={{width: '100%'}} sx={{
                                                alignItems: "top"
                                            }}>
                                                { balance.partialAmt ?
                                                    <>
                                                        <Grid size={6}>
                                                            {`Payment Amt.: ${getDecimal(balance.balancePaymentAmount)}`}
                                                        </Grid>
                                                        <Grid size={6}>
                                                            {`Total Owed: ${getDecimal(balance.courtAmount)}`}
                                                        </Grid>
                                                    </> :
                                                    <Grid size={12}>
                                                        {`Payment Amt.: ${getDecimal(balance.balancePaymentAmount)}`}
                                                    </Grid>
                                                }
                                            </Grid>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                        {/* The totals summarise the list; they are not one of its items (CPDEV-2837). */}
                        { feeResponse &&
                            <Box sx={{margin: '.5rem 1rem', padding: '1rem'}}>
                                <Typography component='div' variant='subtitle2' color='textPrimary'>
                                    <Grid container spacing={2} style={{width: '100%'}} sx={{
                                        alignItems: "top"
                                    }}>
                                        <Grid
                                            style={{padding: '0'}}
                                            size={{
                                                xs: 12,
                                                sm: 4
                                            }}>
                                            {`${nonCourt ? 'Agency' : 'Court'} Amt.: ${getDecimal(feeResponse.courtAmount)}`}
                                        </Grid>
                                        { splitFees ?
                                            <Grid
                                                style={{padding: '0'}}
                                                size={{
                                                    xs: 12,
                                                    sm: 8
                                                }}>
                                                We cannot determine the amount of the Processing Fee until you select a payment type.
                                            </Grid>:
                                            <>
                                                <Grid
                                                    style={{padding: '0'}}
                                                    size={{
                                                        xs: 12,
                                                        sm: 4
                                                    }}>
                                                    {`CitePay Fee: ${getDecimal(feeResponse.citepayFee)}`}
                                                </Grid>
                                                <Grid
                                                    style={{padding: '0'}}
                                                    size={{
                                                        xs: 12,
                                                        sm: 4
                                                    }}>
                                                    {`Total: ${getDecimal(feeResponse.totalAmount)}`}
                                                </Grid>
                                            </>
                                        }
                                    </Grid>
                                </Typography>
                                { serviceFee && serviceFee > 0 ?
                                    <Typography component='div' variant='body1' color='secondary'>
                                        <Grid container spacing={2} style={{width: '100%'}} sx={{
                                            alignItems: "top"
                                        }}>
                                            <Grid sx={{marginTop: '1rem'}} size={12}>
                                                {`A payment plan service fee of $${serviceFee} has been added.`}
                                            </Grid>
                                        </Grid>
                                    </Typography> : null
                                }
                            </Box>
                        }
                        {/* DOM order is the visual order (2.4.3); justifyContent keeps the
                            row right-aligned, which is what row-reverse used to do. */}
                        <Box component='div' sx={{display: 'flex', justifyContent: 'flex-end', margin: '1rem'}}>
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={<PaymentsTwoToneIcon/>}
                                sx={classes.squashedButton}
                                onClick={() => openPayment()}
                                aria-haspopup="dialog"
                            >
                                Pay
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<ReplayIcon/>}
                                sx={classes.squashedButton}
                                onClick={() => clearSelected()}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<ArrowBackIosNewRoundedIcon />}
                                sx={classes.squashedButton}
                                onClick={handleBack}
                            >
                                Back
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}
            {smallScreen && (
                <>
                {/* Each card is the list item; the totals and buttons follow the list (CPDEV-2837). */}
                <List sx={{ padding: 0 }}>
                    {plans && plans.length > 0 && plans.map((plan) => (
                        <Card component='li' elevation={2} sx={{ margin: '1rem 0', padding: '1rem' }} key={plan.planDescription}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        {plan.planDescription}
                                    </Typography>
                                    <IconButton size="small" onClick={() => removePlan(plan)} aria-label={`Remove ${plan.planDescription}`}>
                                        <DeleteTwoTone />
                                    </IconButton>
                                </Box>
                                <Divider />
                                <Grid container spacing={1}>
                                    <Grid size={6}>
                                        <Typography variant="body2" color="textSecondary">Payment Amt.:</Typography>
                                    </Grid>
                                    <Grid sx={{ textAlign: 'right' }} size={6}>
                                        <Typography variant="body2">${getDecimal(plan.planPaymentAmount)}</Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Card>
                    ))}
                    {balances && balances.length > 0 && balances.map((balance) => (
                        <Card component='li' elevation={2} sx={{ margin: '1rem 0', padding: '1rem' }} key={balance.caseNumber}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        {`${getCaseLabel(court)} ${balance.caseNumber}`}
                                    </Typography>
                                    <IconButton size="small" onClick={() => removeBalance(balance)} aria-label={`Remove ${getCaseLabel(court)} ${balance.caseNumber}`}>
                                        <DeleteTwoTone />
                                    </IconButton>
                                </Box>
                                <Divider />
                                <Grid container spacing={1}>
                                    <Grid size={6}>
                                        <Typography variant="body2" color="textSecondary" sx={{
                                            fontWeight: 'bold'
                                        }}>Description:</Typography>
                                    </Grid>
                                    <Grid sx={{ textAlign: 'right' }} size={6}>
                                        <Typography variant="body2">{balance.description}</Typography>
                                    </Grid>
                                    {balance.partialAmt ? (
                                        <>
                                            <Grid size={6}>
                                                <Typography variant="body2" color="textSecondary" sx={{
                                                    fontWeight: 'bold'
                                                }}>Payment Amt.:</Typography>
                                            </Grid>
                                            <Grid sx={{ textAlign: 'right' }} size={6}>
                                                <Typography variant="body2">${getDecimal(balance.balancePaymentAmount)}</Typography>
                                            </Grid>
                                            <Grid size={6}>
                                                <Typography variant="body2" color="textSecondary" sx={{
                                                    fontWeight: 'bold'
                                                }}>Total Owed:</Typography>
                                            </Grid>
                                            <Grid sx={{ textAlign: 'right' }} size={6}>
                                                <Typography variant="body2" sx={{
                                                    fontWeight: 'bold'
                                                }}>${getDecimal(balance.courtAmount)}</Typography>
                                            </Grid>
                                        </>
                                    ) : (
                                        <>
                                            <Grid size={6}>
                                                <Typography variant="body2" color="textSecondary" sx={{
                                                    fontWeight: 'bold'
                                                }}>Payment Amt:</Typography>
                                            </Grid>
                                            <Grid sx={{ textAlign: 'right' }} size={6}>
                                                <Typography variant="body2">${getDecimal(balance.balancePaymentAmount)}</Typography>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Box>
                        </Card>
                        ))}
                </List>
                    {feeResponse && (
                        <Card elevation={2} sx={{ margin: '1rem 0', padding: '1rem' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Grid container spacing={1}>
                                    <Grid size={6}>
                                        <Typography variant="body2" color="textSecondary" sx={{
                                            fontWeight: 'bold'
                                        }}>{`${nonCourt ? 'Agency' : 'Court'} Amt :`}</Typography>
                                    </Grid>
                                    <Grid sx={{ textAlign: 'right' }} size={6}>
                                        <Typography  variant="body2">${getDecimal(feeResponse.courtAmount)}</Typography>
                                    </Grid>
                                    {splitFees ? (
                                        <Grid size={12}>
                                            <Typography variant="body2" color="textSecondary">
                                                We cannot determine the amount of the Processing Fee until you select a payment type.
                                            </Typography>
                                        </Grid>
                                    ) : (
                                        <>
                                            <Grid size={6}>
                                                <Typography variant="body2" color="textSecondary" sx={{
                                                    fontWeight: 'bold'
                                                }}>CitePay Fee:</Typography>
                                            </Grid>
                                            <Grid sx={{ textAlign: 'right' }} size={6}>
                                                <Typography variant="body2">${getDecimal(feeResponse.citepayFee)}</Typography>
                                            </Grid>
                                            <Grid size={6}>
                                                <Typography variant="body2" color="textSecondary"  sx={{
                                                    fontWeight: 'bold'
                                                }}>Total:</Typography>
                                            </Grid>
                                            <Grid sx={{ textAlign: 'right',}} size={6}>
                                                <Typography variant="body2">${getDecimal(feeResponse.totalAmount)}</Typography>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                                { serviceFee && serviceFee > 0 ?
                                    <Grid container spacing={2} style={{width: '100%'}} sx={{
                                        alignItems: "top"
                                    }}>
                                        <Grid sx={{marginTop: '1rem'}} size={12}>
                                            {`A payment plan service fee of $${serviceFee} has been added.`}
                                        </Grid>
                                    </Grid> : null
                                }
                            </Box>
                        </Card>
                    )}
                    <Card elevation={2} sx={{ margin: '1rem 0', padding: '1rem' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <Button variant={smallScreen ? '' : 'contained'} color="primary" onClick={openPayment} sx={smallScreen ? {color: 'success.main', padding: '0'} : {}} aria-haspopup="dialog">
                                Pay
                            </Button>
                            <Button variant={smallScreen ? '' : 'contained'} color="primary" onClick={clearSelected} sx={smallScreen ? {color: 'info.main', padding: '0'} : {}}>
                                Clear
                            </Button>
                            <Button variant={smallScreen ? '' : 'contained'} onClick={handleBack} sx={smallScreen ? {color: 'info.main', padding: '0'} : {}}>
                                Back
                            </Button>
                        </Box>
                    </Card>
                </>
            )}
        </>
    );
}

export default SelectedBalances;